import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Compass, UtensilsCrossed, MessageSquare, Wifi, Clock, Calendar, MapPin, Coffee, User, ClipboardCheck, ChevronRight, Sparkles } from "lucide-react";
import GuestLayout from "@/components/GuestLayout";
import api, { getSettings } from "@/lib/api";
import { getPropertyId, getPropertySlug, PROPERTY_SLUGS } from "@/lib/store";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { mascotBase64 } from "@/assets/mascotBase64";

// Flip Card Component — uses the same guest-card styling as module cards
const FlipCard = ({ item, type }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  
  return (
    <div 
      className="flip-card cursor-pointer perspective-1000"
      onClick={() => setIsFlipped(!isFlipped)}
    >
      <div className={`flip-card-inner relative w-full transition-transform duration-500 transform-style-3d ${isFlipped ? 'rotate-y-180' : ''}`}
           style={{ aspectRatio: '4/3' }}>
        {/* Front */}
        <div className="flip-card-front absolute w-full h-full backface-hidden rounded-xl overflow-hidden shadow-md">
          <div className="relative h-full">
            {item.image_url ? (
              <img 
                src={item.image_url} 
                alt={item.title} 
                className="w-full h-full object-cover" fetchPriority="high" loading="eager" 
                loading="lazy" 
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = type === 'food'
                    ? "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&q=80&w=800"
                    : "https://images.unsplash.com/photo-1590091014590-70dfccb5295e?auto=format&fit=crop&q=80&w=800";
                }}
              />
            ) : (
              <div className={`w-full h-full ${type === 'food' ? 'bg-gradient-to-br from-[#FFC107] to-[#FFC107]' : 'bg-gradient-to-br from-[#2A9D8F] to-[#48bcae]'}`} />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 text-white">
              <h3 className="font-semibold text-sm md:text-base leading-tight line-clamp-2">{item.title}</h3>
              <p className="text-xs opacity-80 line-clamp-2 mt-1 hidden sm:block">{item.description}</p>
              <p className="text-[10px] mt-1 opacity-60">Tap for details</p>
            </div>
          </div>
        </div>
        
        {/* Back */}
        <div className={`flip-card-back absolute w-full h-full backface-hidden rounded-xl overflow-hidden shadow-md rotate-y-180 ${type === 'food' ? 'bg-gradient-to-br from-[#FFC107] to-[#FFC107]' : 'bg-gradient-to-br from-[#2A9D8F] to-[#48bcae]'}`}>
          <div className="p-3 md:p-4 h-full flex flex-col text-white">
            <h3 className="font-semibold text-sm md:text-base mb-1">{item.title}</h3>
            <p className="text-xs flex-1 overflow-auto leading-relaxed">{item.description}</p>
            {item.location && (
              <p className="text-xs mt-1 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {item.location}
              </p>
            )}
            {item.price_range && (
              <p className="text-xs mt-1">Price: {item.price_range}</p>
            )}
            {item.timing && (
              <p className="text-xs mt-1">Timing: {item.timing}</p>
            )}
            {item.tips && (
              <p className="text-[10px] mt-1 italic opacity-80">Tip: {item.tips}</p>
            )}
            <p className="text-[10px] mt-1 opacity-60">Tap to flip back</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function GuestHome() {
  const navigate = useNavigate();
  const { propertySlug } = useParams();

  const propertyId = useMemo(() => {
    if (propertySlug && PROPERTY_SLUGS[propertySlug]) {
      return PROPERTY_SLUGS[propertySlug];
    }
    if (propertySlug && !['experiences', 'food', 'cart', 'requests', 'staff', 'team', 'admin'].includes(propertySlug.toLowerCase())) {
      return propertySlug;
    }
    return getPropertyId();
  }, [propertySlug]);

  const KNOWN_PROPERTY_DEFAULTS = useMemo(() => ({
    "varanasi": {
      property_id: "varanasi",
      property_name: "Hidden Monkey Hostel, Varanasi",
      property_slug: "VaranasiHostel",
      property_location: "Chet Singh Ghat, Varanasi",
    },
    "darjeeling-hostel": {
      property_id: "darjeeling-hostel",
      property_name: "Hidden Monkey Hostel, Darjeeling",
      property_slug: "DarjeelingHostel",
      property_location: "Near Railway Station, Darjeeling",
    },
    "darjeeling-home": {
      property_id: "darjeeling-home",
      property_name: "Hidden Monkey Home, Darjeeling",
      property_slug: "DarjeelingHome",
      property_location: "Batasia Loop, Darjeeling",
    }
  }), []);

  const KNOWN_INFO_DEFAULTS = useMemo(() => ({
    "varanasi": {
      welcome_message: "Welcome to Kashi",
      checkin_time: "2:00 PM",
      checkout_time: "11:00 AM",
    },
    "darjeeling-hostel": {
      welcome_message: "Welcome to the Queen of Hills!",
      checkin_time: "1:00 PM",
      checkout_time: "11:00 AM",
      wifi_name: "Airtel_Hiddenmonkey@01",
      wifi_password: "Hiddenmonkey@01"
    },
    "darjeeling-home": {
      welcome_message: "Welcome to Peaceful Darjeeling",
      checkin_time: "2:00 PM",
      checkout_time: "11:00 AM",
    }
  }), []);

  // 1. Instant Render: Initialize from localStorage (Stale-While-Revalidate) or static defaults
  const [settings, setSettings] = useState(() => {
    try {
      const cached = localStorage.getItem(`hm_settings_${propertyId}`);
      if (cached) return JSON.parse(cached);
    } catch (e) {}
    return KNOWN_PROPERTY_DEFAULTS[propertyId] || null;
  });

  const [propertyInfo, setPropertyInfo] = useState(() => {
    try {
      const cached = localStorage.getItem(`hm_info_${propertyId}`);
      if (cached) return JSON.parse(cached);
    } catch (e) {}
    return KNOWN_INFO_DEFAULTS[propertyId] || null;
  });

  const [loading, setLoading] = useState(!settings || !propertyInfo);
  const [showAllThings, setShowAllThings] = useState(false);
  const [showAllFood, setShowAllFood] = useState(false);
  const [gridCols, setGridCols] = useState(2);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setGridCols(4);
      else if (window.innerWidth >= 768) setGridCols(3);
      else setGridCols(2);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const getVisibleCount = () => {
    if (gridCols === 4) return 4;
    if (gridCols === 3) return 3;
    return 4; // 2 cols * 2 rows on mobile
  };
  
  const visibleCount = getVisibleCount();

  useEffect(() => {
    let isMounted = true;
    const init = async () => {
      try {
        if (propertySlug && !PROPERTY_SLUGS[propertySlug]) {
          const propsRes = await api.get("/properties");
          const exists = Array.isArray(propsRes.data) && propsRes.data.some(
            p => p.property_id === propertySlug || (p.property_slug && p.property_slug.toLowerCase() === propertySlug.toLowerCase())
          );
          if (!exists) {
            navigate("/", { replace: true });
            return;
          }
        }

        // 2. Parallelize network requests without cache-busting timestamp
        const [settingsData, infoRes] = await Promise.all([
          getSettings(propertyId).catch(() => null),
          api.get(`/property-info/${propertyId}`).catch(() => ({ data: null }))
        ]);

        if (isMounted) {
          if (settingsData) {
            setSettings(settingsData);
            try { localStorage.setItem(`hm_settings_${propertyId}`, JSON.stringify(settingsData)); } catch (e) {}
          }
          if (infoRes?.data) {
            setPropertyInfo(infoRes.data);
            try { localStorage.setItem(`hm_info_${propertyId}`, JSON.stringify(infoRes.data)); } catch (e) {}
          }
        }
      } catch (error) {
        console.error("Error initializing:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    init();
    return () => { isMounted = false; };
  }, [propertyId, propertySlug, navigate]);

  // Default images
  const defaultImages = {
    experiences: "https://images.unsplash.com/photo-1590091014590-70dfccb5295e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    food: "https://images.pexels.com/photos/7420939/pexels-photo-7420939.jpeg?auto=compress&cs=tinysrgb&w=800",
    requests: "https://images.pexels.com/photos/32501209/pexels-photo-32501209.jpeg?auto=compress&cs=tinysrgb&w=800",
    header: "https://images.unsplash.com/photo-1683914791867-7d65ac8893de?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
  };

  const modules = [
    {
      id: "experiences",
      title: "Experiences",
      subtitle: "Curated adventures & activities",
      icon: Compass,
      image: propertyInfo?.experiences_header_image || defaultImages.experiences,
      path: "/experiences",
      color: "from-[#2A9D8F]/80 to-[#48bcae]/90",
    },
    {
      id: "food",
      title: "Food & Drinks",
      subtitle: "Fresh, local, delicious",
      icon: UtensilsCrossed,
      image: propertyInfo?.food_header_image || defaultImages.food,
      path: "/food",
      color: "from-[#FFC107]/80 to-[#FFC107]/90",
    },
    {
      id: "requests",
      title: "Requests",
      subtitle: "How can we help?",
      icon: MessageSquare,
      image: propertyInfo?.requests_header_image || defaultImages.requests,
      path: "/requests",
      color: "from-[#264653]/80 to-[#2A9D8F]/90",
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex flex-col items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2A9D8F] border-t-transparent mb-4"></div>
        <p className="text-[#264653] font-medium animate-pulse">Loading property...</p>
      </div>
    );
  }

  return (
    <GuestLayout>
      <main className="min-h-screen pb-12" data-testid="guest-home">
        {/* Hero Section with Logo */}
        <div className="relative h-[38vh] min-h-[300px] overflow-hidden">
          {/* Team Login - Top Corner of Hero */}
          <button
            onClick={() => navigate(`/team/login?property=${propertyId}`)}
            className="absolute top-4 right-4 md:top-6 md:right-8 z-20 text-sm md:text-base font-bold px-5 py-2.5 md:px-8 md:py-3 rounded-full border-2 border-white/40 text-white hover:bg-white/20 transition-all shadow-md backdrop-blur-md"
            data-testid="team-login-btn"
            title="Login"
          >
            Login
          </button>

          <img
            src={(propertyInfo?.header_image && !propertyInfo.header_image.includes("photo-1469854523086")) ? propertyInfo.header_image : defaultImages.header}
            alt="Hidden Monkey Stays"
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = defaultImages.header;
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#2A9D8F]/40 via-[#2A9D8F]/60 to-[#264653]/95" />
          
          <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-12">
            {/* Mascot Logo */}
            <div className="absolute top-4 left-4 md:top-6 md:left-6">
              <img 
                src={mascotBase64}
                alt="Hidden Monkey" 
                className="w-32 h-32 md:w-40 md:h-40 object-contain mascot-float drop-shadow-lg"
              />
            </div>
            
            <p className="font-accent text-[#FFC107] text-lg md:text-xl mb-1 animate-fade-in">
              {propertyInfo?.welcome_message || KNOWN_INFO_DEFAULTS[propertyId]?.welcome_message || "Welcome to"}
            </p>
            <h1 
              className="font-accent text-3xl md:text-5xl text-white mb-1 animate-fade-in-up font-bold"
              data-testid="property-name"
            >
              {settings?.property_name || KNOWN_PROPERTY_DEFAULTS[propertyId]?.property_name || "Hidden Monkey"}
            </h1>
            {(settings?.property_location || KNOWN_PROPERTY_DEFAULTS[propertyId]?.property_location) && (
              <p className="text-white/70 text-sm md:text-base flex items-center gap-1">
                <MapPin className="w-4 h-4" /> {settings?.property_location || KNOWN_PROPERTY_DEFAULTS[propertyId]?.property_location}
              </p>
            )}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 backdrop-blur-md text-white text-xs font-semibold w-fit mt-2 border border-white/20 shadow-sm">
              <Sparkles className="w-3.5 h-3.5 text-[#FFC107]" /> Powered by Koko • Your In-Stay Companion
            </div>
          </div>
        </div>

        {/* Check-in Banner */}
        {propertyInfo?.checkin_form_url && (
          <div className="px-4 md:px-8 -mt-4 relative z-20 mb-4">
            <button
              onClick={() => navigate(`/${getPropertySlug()}/checkin`)}
              className="w-full bg-gradient-to-r from-[#FFC107] to-[#FFC107] rounded-2xl p-3 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] active:scale-95"
              data-testid="checkin-banner"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <ClipboardCheck className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-white font-bold text-base">Complete Your Check-in</h3>
                    <p className="text-white/80 text-xs">Quick & easy registration</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-white" />
              </div>
            </button>
          </div>
        )}

        {/* Property Info Cards */}
        {propertyInfo && (
          <div className="px-4 md:px-8 -mt-6 relative z-10 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="bg-white/90 backdrop-blur-sm border-none shadow-md">
                <CardContent className="p-3 text-center">
                  <Clock className="w-5 h-5 mx-auto text-[#2A9D8F] mb-1" />
                  <p className="text-xs text-gray-500">Check-in</p>
                  <p className="font-semibold text-sm text-[#264653]">{propertyInfo.checkin_time || "2:00 PM"}</p>
                </CardContent>
              </Card>
              <Card className="bg-white/90 backdrop-blur-sm border-none shadow-md">
                <CardContent className="p-3 text-center">
                  <Clock className="w-5 h-5 mx-auto text-[#FFC107] mb-1" />
                  <p className="text-xs text-gray-500">Check-out</p>
                  <p className="font-semibold text-sm text-[#264653]">{propertyInfo.checkout_time || "11:00 AM"}</p>
                </CardContent>
              </Card>
              {propertyInfo.wifi_name && (
                <>
                  <Card 
                    className="bg-white/90 backdrop-blur-sm border-none shadow-md cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => {
                      navigator.clipboard.writeText(propertyInfo.wifi_name);
                      toast.success("WiFi Name copied!");
                    }}
                  >
                    <CardContent className="p-3 text-center flex flex-col items-center justify-center h-full">
                      <Wifi className="w-5 h-5 mx-auto text-[#FFC107] mb-1" />
                      <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider mb-0.5">WiFi Name</p>
                      <p className="font-semibold text-xs md:text-sm text-[#264653] break-all leading-tight">{propertyInfo.wifi_name}</p>
                    </CardContent>
                  </Card>
                  <Card 
                    className="bg-white/90 backdrop-blur-sm border-none shadow-md cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => {
                      navigator.clipboard.writeText(propertyInfo.wifi_password);
                      toast.success("WiFi Password copied!");
                    }}
                  >
                    <CardContent className="p-3 text-center flex flex-col items-center justify-center h-full">
                      <Wifi className="w-5 h-5 mx-auto text-[#6BB8B2] mb-1" />
                      <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider mb-0.5">Password</p>
                      <p className="font-semibold text-xs md:text-sm text-[#264653] break-all leading-tight">{propertyInfo.wifi_password}</p>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          </div>
        )}

        {/* Today's Events */}
        {Array.isArray(propertyInfo?.today_events) && propertyInfo.today_events.length > 0 && (
          <div className="px-4 md:px-8 mb-6">
            <h2 className="font-accent text-xl text-[#2A9D8F] mb-3 flex items-center gap-2 font-bold">
              <Calendar className="w-5 h-5 text-[#2A9D8F]" /> Today's Events
            </h2>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {propertyInfo.today_events.map((event, i) => (
                <Card key={i} className="min-w-[200px] bg-gradient-to-br from-[#2A9D8F]/10 to-[#FFC107]/10 border-none">
                  <CardContent className="p-4">
                    <p className="font-semibold text-[#264653]">{event.title}</p>
                    <p className="text-sm text-[#2A9D8F]">{event.time}</p>
                    {event.description && <p className="text-xs text-gray-600 mt-1">{event.description}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Module Cards: Experiences & Food Side-by-Side + Full Width Requests Banner */}
        <div className="px-4 md:px-8 mb-6">
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            {modules
              .filter(m => m.id === "experiences" || m.id === "food")
              .filter(m => {
                if (m.id === "experiences" && settings?.experiences_enabled === false) return false;
                if (m.id === "food" && settings?.food_enabled === false) return false;
                return true;
              })
              .map((module) => (
                <button
                  key={module.id}
                  onClick={() => navigate(`${module.path}?property=${propertyId}`)}
                  className="guest-card group text-left overflow-hidden rounded-2xl bg-white shadow-xs hover:shadow-md transition-all border border-[#E8E4D9]/60 flex flex-col"
                  data-testid={`module-${module.id}`}
                >
                  <div className="relative h-36 md:h-48 overflow-hidden w-full">
                    <img
                      src={module.image}
                      alt={module.title}
                      className="w-full h-full object-cover img-zoom"
                    loading="lazy" />
                    <div className={`absolute inset-0 bg-gradient-to-t ${module.color} opacity-60 group-hover:opacity-75 transition-opacity duration-300`} />
                    <div className="absolute top-3 right-3 w-9 h-9 rounded-xl bg-white/25 backdrop-blur-sm flex items-center justify-center shadow-xs">
                      <module.icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div className="p-3.5 md:p-4 bg-white flex-1 flex flex-col justify-between">
                    <div>
                      <h2 className="font-accent text-base md:text-lg text-[#264653] font-bold leading-tight">
                        {module.title}
                      </h2>
                      <p className="text-xs text-[#636E72] mt-1 line-clamp-1">{module.subtitle}</p>
                    </div>
                    <span className="text-[11px] font-bold text-[#2A9D8F] mt-2 inline-flex items-center gap-0.5">
                      Explore <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </button>
              ))}
          </div>

          {/* Requests - Full Width Concierge Banner (Eliminates Empty Space) */}
          {settings?.requests_enabled !== false && (
            <button
              onClick={() => navigate(`/requests?property=${propertyId}`)}
              className="w-full guest-card group text-left overflow-hidden rounded-2xl p-3.5 md:p-4 bg-white border border-[#E8E4D9]/60 shadow-xs hover:shadow-md transition-all flex items-center justify-between gap-3.5 mt-3"
              data-testid="module-requests"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="relative w-12 h-12 md:w-14 md:h-14 rounded-xl overflow-hidden flex-shrink-0 bg-[#2A9D8F]/10">
                  <img
                    src={propertyInfo?.requests_header_image || defaultImages.requests}
                    alt="Requests"
                    className="w-full h-full object-cover img-zoom"
                    onError={(e) => { e.target.onerror = null; e.target.src = defaultImages.requests; }}
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-[#264653]/40 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div className="min-w-0">
                  <h2 className="font-accent text-sm md:text-base text-[#264653] font-bold leading-tight">
                    Guest Requests & Assistance
                  </h2>
                  <p className="text-xs text-[#636E72] mt-0.5 truncate">
                    Housekeeping, laundry, room service, or questions
                  </p>
                </div>
              </div>
              <div className="w-8 h-8 rounded-full bg-[#2A9D8F]/10 flex items-center justify-center text-[#2A9D8F] flex-shrink-0 group-hover:bg-[#2A9D8F] group-hover:text-white transition-colors">
                <ChevronRight className="w-4 h-4" />
              </div>
            </button>
          )}
        </div>

        {/* Things to Do Section */}
        {Array.isArray(propertyInfo?.things_to_do) && propertyInfo.things_to_do.length > 0 && (
          <div className="px-4 md:px-8 mb-8">
            <h2 className="font-accent text-xl text-[#2A9D8F] mb-4 flex items-center gap-2 font-bold">
              <MapPin className="w-5 h-5 text-[#2A9D8F]" /> Things to Do
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {(showAllThings ? propertyInfo.things_to_do : propertyInfo.things_to_do.slice(0, visibleCount)).map((item, i) => (
                <FlipCard key={i} item={item} type="things" />
              ))}
            </div>
            {propertyInfo.things_to_do.length > visibleCount && (
              <div className="mt-6 text-center">
                <button
                  onClick={() => setShowAllThings(!showAllThings)}
                  className="px-6 py-2.5 bg-[#2A9D8F] text-white rounded-full font-semibold hover:bg-[#218377] transition-colors text-sm shadow-md"
                >
                  {showAllThings ? "Show Less" : `View All ${propertyInfo.things_to_do.length} Things to Do`}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Food to Try Section */}
        {Array.isArray(propertyInfo?.food_to_try) && propertyInfo.food_to_try.length > 0 && (
          <div className="px-4 md:px-8 pb-8">
            <h2 className="font-accent text-xl text-[#FFC107] mb-4 flex items-center gap-2 font-bold">
              <Coffee className="w-5 h-5 text-[#FFC107]" /> Where to Eat & What to Try
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {(showAllFood ? propertyInfo.food_to_try : propertyInfo.food_to_try.slice(0, visibleCount)).map((item, i) => (
                <FlipCard key={i} item={item} type="food" />
              ))}
            </div>
            {propertyInfo.food_to_try.length > visibleCount && (
              <div className="mt-6 text-center">
                <button
                  onClick={() => setShowAllFood(!showAllFood)}
                  className="px-6 py-2.5 bg-[#FFC107] text-white rounded-full font-semibold hover:bg-[#e09520] transition-colors text-sm shadow-md"
                >
                  {showAllFood ? "Show Less" : `View All ${propertyInfo.food_to_try.length} Recommendations`}
                </button>
              </div>
            )}
          </div>
        )}
      </main>
    </GuestLayout>
  );
}
